import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, AlertTriangle, CheckCircle2, Clock, Shield, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { useBoards } from '../context/BoardContext';
import { listIssues } from '../lib/api';
import {
  CATEGORY_MAP, DEFAULT_LEGEND, getCategoryConfig, TASK_STATUSES,
} from '../lib/categories';
import { toISODate } from '../lib/date';
import IssueEditDialog from './IssueEditDialog';

const OpenIssuesView = ({ initialFilter }) => {
  const { activeBoard } = useBoards();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(initialFilter || 'open');
  const [editor, setEditor] = useState({ open: false, existing: null, categoryKey: null, date: null });

  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);

  const load = useCallback(async () => {
    if (!activeBoard) return;
    setLoading(true);
    try {
      const params = {};
      if (filter === 'open') params.open_only = true;
      else if (filter === 'closed') params.status = 'closed';
      // 'all' → no filter
      const data = await listIssues(activeBoard.id, params);
      // Add overdue flag
      const today = toISODate(new Date());
      const enhanced = data.map((i) =>
        !i.is_closed && i.target_date && i.target_date < today
          ? { ...i, _overdue: true }
          : i
      );
      setIssues(enhanced);
    } finally {
      setLoading(false);
    }
  }, [activeBoard, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = {
    open: issues.filter((i) => !i.is_closed && i.color !== 'green' && i.color !== 'grey').length,
    closed: issues.filter((i) => i.is_closed).length,
    overdue: issues.filter((i) => i._overdue).length,
    all: issues.length,
  };

  const handleEdit = (issue) => {
    setEditor({ open: true, existing: issue, categoryKey: issue.category, date: issue.date });
  };

  const handleEditorClose = (changed) => {
    setEditor({ open: false, existing: null, categoryKey: null, date: null });
    if (changed) load();
  };

  if (!activeBoard) {
    return <div className="p-12 text-center text-slate-500">Select or create a board first.</div>;
  }

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Open Issues</h1>
          <p className="text-sm text-slate-500 mt-1">
            All issues with a responsible owner and target date, from {activeBoard.name}.
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: 'open', label: 'Open', count: counts.open },
          { key: 'closed', label: 'Closed', count: counts.closed },
          { key: 'overdue', label: 'Overdue', count: counts.overdue },
          { key: 'all', label: 'All', count: counts.all },
        ].map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                active
                  ? 'bg-[#0090D8] text-white border-[#0090D8]'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              }`}
            >
              {f.label}
              <span className={`ml-2 text-xs ${active ? 'text-white/80' : 'text-slate-500'}`}>
                {f.count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading issues...
        </div>
      ) : (
        <div className="grid gap-3">
          {(filter === 'overdue' ? issues.filter((i) => i._overdue) : issues).length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500">
              No {filter === 'all' ? '' : filter} issues.
            </div>
          ) : (
            (filter === 'overdue' ? issues.filter((i) => i._overdue) : issues).map((issue) => {
              const cat = CATEGORY_MAP[issue.category];
              const catConfig = getCategoryConfig(activeBoard, issue.category);
              const statusEntry =
                (catConfig?.statuses || DEFAULT_LEGEND).find((s) => s.key === issue.color) ||
                DEFAULT_LEGEND[3];
              const hasTemp = (issue.temporary_cm || '').length > 0;
              const hasPerm = (issue.permanent_cm || '').length > 0;
              return (
                <button
                  key={issue.id}
                  onClick={() => handleEdit(issue)}
                  className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md hover:-translate-y-0.5 transition-all text-left group"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-11 h-11 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-sm flex-shrink-0"
                      style={{ background: cat?.color || '#64748b' }}
                    >
                      {issue.category}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                          style={{ background: statusEntry.bg, color: statusEntry.ring || statusEntry.color }}
                        >
                          {statusEntry.label}
                        </span>
                        <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">
                          {cat?.label}
                        </span>
                        {issue._overdue && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase bg-red-100 text-red-800 border border-red-300">
                            OVERDUE
                          </span>
                        )}
                        {issue.repeat_issue && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                            <AlertTriangle className="h-2.5 w-2.5" />
                            Repeat
                          </span>
                        )}
                        {issue.is_closed && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-300">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            Closed
                          </span>
                        )}
                        {hasTemp && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300">
                            <Clock className="h-2.5 w-2.5" /> Temp CM
                          </span>
                        )}
                        {hasPerm && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300">
                            <Shield className="h-2.5 w-2.5" /> Perm CM
                          </span>
                        )}
                      </div>
                      <div className="font-semibold text-slate-900 mb-1">
                        {Object.values(issue.details || {}).filter(Boolean).slice(0, 3).join(' · ') ||
                          `${cat?.label} issue on ${issue.date}`}
                      </div>
                      {issue.notes && (
                        <div className="text-sm text-slate-600 line-clamp-2 mb-2">
                          {issue.notes}
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                        <span>📅 {issue.date}</span>
                        {issue.owner_email && <span>👤 {issue.owner_email}</span>}
                        {issue.target_date && (
                          <span className={issue._overdue ? 'text-red-600 font-semibold' : ''}>
                            🎯 Due {issue.target_date}
                          </span>
                        )}
                        {issue.status && (
                          <span className="capitalize">Status: {issue.status.replace('_', ' ')}</span>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1 group-hover:text-slate-600" />
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}

      <IssueEditDialog
        open={editor.open}
        onOpenChange={(v) => { if (!v) handleEditorClose(false); }}
        onSaved={() => handleEditorClose(true)}
        onDeleted={() => handleEditorClose(true)}
        categoryKey={editor.categoryKey}
        date={editor.date}
        existing={editor.existing}
      />
    </div>
  );
};

export default OpenIssuesView;
